import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Users, Zap, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useForceDark } from "@/hooks/useForceDark";

const HERO = "https://images.pexels.com/photos/38454829/pexels-photo-38454829.jpeg";
const CELEBRATION = "https://images.pexels.com/photos/34764171/pexels-photo-34764171.jpeg";
const OUTDOOR = "https://images.pexels.com/photos/33210166/pexels-photo-33210166.jpeg";

export default function Landing() {
  useForceDark();
  const { siteSettings } = useAuth();
  // Treat "still loading" (null) as "hidden" — safer than briefly flashing a
  // Create-account button on a private club's landing page.
  const allowRegister = !!(siteSettings && siteSettings.self_registration_enabled);
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white grain">
      {/* Nav */}
      <header className="relative z-20 backdrop-blur-xl bg-[#0A0F1C]/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <img src="/logo.png" alt="Elsenham YFC" className="w-11 h-11 rounded-full object-contain" />
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">ELSENHAM YFC</div>
              <div className="text-[10px] tracking-[0.3em] text-white/50 uppercase mt-1">Space Booking</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-white/70 hover:text-white px-4 py-2" data-testid="landing-login-link">Sign in</Link>
            {allowRegister && (
              <Link to="/register" data-testid="landing-register-link" className="btn-volt px-5 py-2.5 text-sm rounded-md inline-flex items-center gap-2">
                Get started <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" aria-hidden className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F1C] via-[#0A0F1C]/70 to-[#0A0F1C]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 md:py-44 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#83B68B]/40 bg-[#83B68B]/10 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#83B68B] animate-pulse"></span>
              <span className="text-xs tracking-[0.25em] uppercase text-[#83B68B]">Elsenham Youth FC · Est. Community</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl mt-6 leading-[0.95]">
              Book the pitch.<br/>
              <span className="text-[#83B68B]">Own the session.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/70">
              Instant online booking for training, matches and squad sessions across every Elsenham Youth FC space —
              grass, 3G, indoor.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              {allowRegister && (
                <Link to="/register" data-testid="landing-cta-register" className="btn-volt px-7 py-4 text-base rounded-md inline-flex items-center gap-2">
                  Create account <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              <Link to="/login" data-testid="landing-cta-signin" className="px-7 py-4 text-base rounded-md border border-white/20 hover:border-white/60 transition-colors">
                Sign in
              </Link>
            </div>
          </div>
          <div className="hidden md:block md:col-span-4">
            <div className="grid grid-cols-2 gap-3 mt-16">
              {[
                {label:"Pitches", value:"4"},
                {label:"Open Slots / Wk", value:"312"},
                {label:"Squads", value:"18"},
                {label:"Uptime", value:"24/7"},
              ].map(s => (
                <div key={s.label} className="stat-tile p-5 rounded-md">
                  <div className="font-display font-black text-4xl text-[#83B68B]">{s.value}</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">System</div>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-3 leading-tight">Elsenham YFC official club booking platform.</h2>
            <p className="mt-4 text-white/70">Every feature keeps your Saturday running.</p>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              {icon: Calendar, title:"Live availability", body:"Hour-by-hour slot view across every pitch — no more double bookings."},
              {icon: Users, title:"Team rosters", body:"Manage age groups, coaches and player numbers in one place."},
              {icon: Zap, title:"Instant confirmation", body:"Email confirmations go out the moment a slot is locked in."},
              {icon: Shield, title:"Admin oversight", body:"Committee-level dashboard for utilisation, revenue and users."},
            ].map(({icon:Icon, title, body}, i) => (
              <div key={title} className="card-tactical p-6 rounded-md">
                <Icon className="w-6 h-6 text-[#83B68B]" />
                <div className="font-display font-bold text-xl mt-4 tracking-tight">{title}</div>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Split imagery */}
      <section className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-7 relative overflow-hidden rounded-md">
            <img src={OUTDOOR} alt="Outdoor pitch" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Grass · 3G · Indoor</div>
              <div className="font-display font-black text-3xl mt-2">Every surface, one hub.</div>
            </div>
          </div>
          <div className="md:col-span-5 relative overflow-hidden rounded-md">
            <img src={CELEBRATION} alt="Team celebration" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Community</div>
              <div className="font-display font-black text-3xl mt-2">Built for the club, by the club.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      {allowRegister && (
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-4xl md:text-6xl leading-tight">Ready to lock in the season?</h2>
          <p className="mt-6 text-white/70">Register in 30 seconds and start booking.</p>
          <Link to="/register" data-testid="landing-cta-bottom" className="btn-volt inline-flex mt-10 px-8 py-4 text-base rounded-md items-center gap-2">
            Create free account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      )}

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
          <div>© {new Date().getFullYear()} Elsenham Youth Football Club</div>
          <div className="font-display tracking-widest text-xs">EST · TRAIN · PLAY</div>
        </div>
      </footer>
    </div>
  );
}
